export interface KPIDelta {
    current: number;
    previous: number;
    changePct: number;
}

export interface AdvancedKPIData {
    marginPct: number;
    grossSales: number;
    returnAmount: number;
    returnCount: number;
    returnRatePct: number;
    supplyGross: number;
    supplyReturnAmount: number;
    supplyReturnCount: number;
    supplyReturnRatePct: number;
    periodInvoiced: number;
    periodPaid: number;
    outstandingCreatedInPeriod: number;
    collectionRatePct: number;
    netCollected: number;
    pendingDepositsPct: number;
    unreconciledPaymentCount: number;
    supplyToSalesRatio: number;
    shrinkageValue: number;
    shrinkageCount: number;
    outOfStockCount: number;
    stockCoverageDays: number | null;
    openInvoiceCount: number;
    openInvoiceValue: number;
    periodSupplied: number;
    supplierPeriodPaid: number;
    supplierPaymentRatePct: number;
    openPayablesValue: number;
    overdueSupplyCount: number;
    supplierCountWithBalance: number;
    openPayablesCount: number;
}

export interface DashboardAlert {
    id: string;
    severity: 'high' | 'medium' | 'low';
    category: string;
    title: string;
    message: string;
    params?: Record<string, string | number>;
}

export interface DualTrendPoint {
    date: string;
    salesAmount: number;
    paymentAmount: number;
    isForecast?: boolean;
}

export interface SalesReturnTrendPoint {
    date: string;
    grossSales: number;
    returnAmount: number;
}

export interface SupplyReturnTrendPoint {
    date: string;
    grossSupplies: number;
    returnAmount: number;
}

export interface OverstockItem {
    depot: string;
    item: string;
    stock: number;
    value: number;
}

export interface InventoryHealthData {
    outOfStockCount: number;
    stockCoverageDays: number | null;
    byDepot: Array<{
        depot: string;
        outOfStockCount: number;
        lowStockCount: number;
        totalValue: number;
    }>;
    overstockItems: OverstockItem[];
}

export interface ConversionTypeImpact {
    type: string;
    count: number;
    value: number;
}

export interface ConversionTopItem {
    source: string;
    dest: string;
    type: string;
    count: number;
    value: number;
}

export interface InvoiceAgingBucket {
    bucket: string;
    count: number;
    value: number;
}

export interface AgentScorecardRow {
    agent: string;
    sales: number;
    collections: number;
    netDebtChange: number;
    returnRatePct: number;
}

export interface SalesHeatmapDay {
    day: string;
    amount: number;
    count: number;
}

export interface TrendPoint {
    date: string;
    amount: number;
    count?: number;
}

export interface PayablesAgingBucket {
    bucket: string;
    count: number;
    value: number;
}

export interface AdvancedDashboardData {
    kpi: AdvancedKPIData;
    kpiDeltas: Record<string, KPIDelta>;
    paymentTrend: TrendPoint[];
    pendingDepositsTrend: TrendPoint[];
    dualTrend: DualTrendPoint[];
    salesReturnTrend: SalesReturnTrendPoint[];
    supplyReturnTrend: SupplyReturnTrendPoint[];
    inventoryHealth: InventoryHealthData;
    conversionImpact: {
        byType: ConversionTypeImpact[];
        topItems: ConversionTopItem[];
    };
    openInvoices: {
        openInvoiceCount: number;
        openInvoiceValue: number;
        aging: InvoiceAgingBucket[];
    };
    agentScorecard: AgentScorecardRow[];
    salesHeatmap: SalesHeatmapDay[];
    salesForecast: Array<{
        date: string;
        amount: number;
        movingAvg: number;
        isForecast: boolean;
    }>;
    supplierPaymentTrend: TrendPoint[];
    openPayables: {
        openPayablesCount: number;
        openPayablesValue: number;
        aging: PayablesAgingBucket[];
        overdueSupplies: Array<{
            batch: string;
            supplier: string;
            balance: number;
            dueDate: string;
        }>;
    };
    alerts: DashboardAlert[];
}

export const emptyAdvancedData: AdvancedDashboardData = {
    kpi: {
        marginPct: 0,
        grossSales: 0,
        returnAmount: 0,
        returnCount: 0,
        returnRatePct: 0,
        supplyGross: 0,
        supplyReturnAmount: 0,
        supplyReturnCount: 0,
        supplyReturnRatePct: 0,
        periodInvoiced: 0,
        periodPaid: 0,
        outstandingCreatedInPeriod: 0,
        collectionRatePct: 0,
        netCollected: 0,
        pendingDepositsPct: 0,
        unreconciledPaymentCount: 0,
        supplyToSalesRatio: 0,
        shrinkageValue: 0,
        shrinkageCount: 0,
        outOfStockCount: 0,
        stockCoverageDays: null,
        openInvoiceCount: 0,
        openInvoiceValue: 0,
        periodSupplied: 0,
        supplierPeriodPaid: 0,
        supplierPaymentRatePct: 0,
        openPayablesValue: 0,
        overdueSupplyCount: 0,
        supplierCountWithBalance: 0,
        openPayablesCount: 0,
    },
    kpiDeltas: {},
    paymentTrend: [],
    pendingDepositsTrend: [],
    dualTrend: [],
    salesReturnTrend: [],
    supplyReturnTrend: [],
    inventoryHealth: {
        outOfStockCount: 0,
        stockCoverageDays: null,
        byDepot: [],
        overstockItems: [],
    },
    conversionImpact: { byType: [], topItems: [] },
    openInvoices: { openInvoiceCount: 0, openInvoiceValue: 0, aging: [] },
    agentScorecard: [],
    salesHeatmap: [],
    salesForecast: [],
    supplierPaymentTrend: [],
    openPayables: {
        openPayablesCount: 0,
        openPayablesValue: 0,
        aging: [],
        overdueSupplies: [],
    },
    alerts: [],
};